import { Card, Grid, CrudMode, useOrderableList, Stack, IconButton, Tooltip, Typography, ValidationErrors } from "@compass/core";
import {
  Form,
  FormAnswer,
  FormQuestion,
  FormSection,
  FormSubQuestion,
  SurveyEditResource,
  SurveyViewResource,
} from "generated/openApi";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { SurveyQuestionContainer } from "./SurveyQuestionContainer";
import { useEffect, useState } from "react";
import { createEntity } from "utils/CreateEntity";

export const SurveyQuestions = (props: {
  survey: Form;
  copySurvey: number | undefined;
  setSurvey: (value: Form) => void;
  // updateQuestions: (value: FormQuestion[]) => void;
  viewResource: SurveyViewResource;
  editResource: SurveyEditResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
  refreshSurvey: Form | undefined;
  navigateEditMode: () => void;
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState<FormQuestion | null>();
  const [questions, setQuestions] = useState<FormQuestion[]>();

  useEffect(() => {
    setQuestions(props.survey.formQuestions || []);
  }, []);

  useEffect(() => {
    setQuestions(props.survey.formQuestions || []);
  }, [props.copySurvey]);

  useEffect(() => {
    if (props.refreshSurvey) {
      setQuestions(props.refreshSurvey?.formQuestions || []);
    }
  }, [props.refreshSurvey]);

  useEffect(() => {
    props.setSurvey(
      produce(props.survey, (draft) => {
        if (draft) {
          draft.formQuestions = questions!;
        }
      })
    );
  }, [questions]);

  const addQuestion = (displayOrder: number) => {
    setQuestions(
      produce((draft) => {
        let newQuestion: FormQuestion = createEntity<FormQuestion>({
          displayOrder,
          isMandatory: false,
        });

        // increase the display order of all questions after the newly added question
        var increasingQuestions = draft?.filter((q) => q.displayOrder >= displayOrder);
        increasingQuestions?.forEach((q) => q.displayOrder++);

        draft?.push(newQuestion);

        draft?.sort((a, b) => a.displayOrder - b.displayOrder);

        setSelectedQuestion(newQuestion);
      })
    );
  };

  const copyQuestion = (displayOrder: number, copiedQuestion: FormQuestion) => {
    setQuestions(
      produce((draft) => {
        let newQuestion: FormQuestion = createEntity<FormQuestion>({
          name: copiedQuestion.name,
          label: copiedQuestion.label,
          instruction: copiedQuestion.instruction,
          formQuestionTypeId: copiedQuestion.formQuestionTypeId,
          formQuestionWriteInTypeId: copiedQuestion.formQuestionWriteInTypeId,
          hideSubQuestionLabel: copiedQuestion.hideSubQuestionLabel,
          hideLabel: copiedQuestion.hideLabel,
          hideQuestionName: copiedQuestion.hideQuestionName,
          hideSubQuestionName: copiedQuestion.hideSubQuestionName,
          isAlternatingRowColor: copiedQuestion.isAlternatingRowColor,
          displayOrder,
          isMandatory: copiedQuestion.isMandatory,
        });

        // add answers
        var copiedAnswers: FormAnswer[] = [];
        copiedQuestion.formAnswers?.forEach((a) => {
          let newAnswer: FormAnswer = createEntity<FormAnswer>({
            name: a.name,
            displayOrder: a.displayOrder,
            isOther: a.isOther,
            otherWidthPercent: a.otherWidthPercent,
          });
          copiedAnswers.push(newAnswer);
        });
        // add subQuestions
        var copiedSubQuestions: FormSubQuestion[] = [];
        copiedQuestion.formSubQuestions?.forEach((a) => {
          let newSubQuestion: FormSubQuestion = createEntity<FormSubQuestion>({
            name: a.name,
            label: a.label,
            displayOrder: a.displayOrder,
            isOther: a.isOther,
          });
          copiedSubQuestions.push(newSubQuestion);
        });
        newQuestion.formAnswers = copiedAnswers;
        newQuestion.formSubQuestions = copiedSubQuestions;

        // increase the display order of all questions after the newly added question
        var increasingQuestions = draft?.filter((q) => q.displayOrder >= displayOrder);
        increasingQuestions?.forEach((q) => q.displayOrder++);

        draft?.push(newQuestion);
        draft?.sort((a, b) => a.displayOrder - b.displayOrder);

        setSelectedQuestion(newQuestion);
      })
    );
  };

  const updateQuestion = (question: FormQuestion) => {
    setQuestions(
      produce((draft) => {
        let draftQuestionIndex = draft?.findIndex(
          (q) => q.id == question.id && q.displayOrder == question.displayOrder
        );

        draft![draftQuestionIndex!] = question;
      })
    );
  };

  const getBranchableQuestions = (question: FormQuestion) : FormQuestion[] => {
    if (!questions) return [];
    return questions?.filter(function (e) { return e.id !== question.id && e.tempId !== question.tempId; });
  }

  const getBranchableSections = () : FormSection[] => {
    return props.survey.formSections || [];
  }

  const renderQuestions = (question: FormQuestion, index: number) => {
    const formQuestionValidationErrors = props.validationErrors[`formQuestions`];
    let questionValidationErrors = {};
    if (formQuestionValidationErrors) {
      questionValidationErrors = formQuestionValidationErrors[index];
    }

    return (
      <SurveyQuestionContainer
        viewResource={props.viewResource}
        question={question}
        refreshSurvey={props.refreshSurvey}
        updateQuestion={updateQuestion}
        copyQuestion={copyQuestion}
        addQuestion={addQuestion}
        selected={selectedQuestion?.id == question.id && selectedQuestion?.displayOrder == question.displayOrder}
        setSelected={setSelectedQuestion}
        key={`${question.id}:${question.displayOrder}`}
        navigateEditMode={props.navigateEditMode}
        validationErrors={questionValidationErrors}
        branchableQuestions={getBranchableQuestions(question)}
        branchableSections={getBranchableSections()}
      />
    );
  };

  return (
    <Grid container spacing="lg">
      {props.survey && questions?.filter((q) => !q.deleted).map((q, i) => renderQuestions(q, i))}
      {props.survey && questions?.length == 0 && (
        <Grid item xs={10} display="flex" alignItems="center" justifyContent="center">
          <Card>
            <Typography>No questions yet, click to begin!</Typography>
            <IconButton onClick={() => addQuestion(1)}>
              <Tooltip title="Add New Question">
                <AddCircleOutlineIcon />
              </Tooltip>
            </IconButton>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};
